/**
 * Anime Cart JS - AJAX cart functionality for Plot Twist Essentials
 * Handles all cart operations without page reloads
 */

window.AnimeCart = (function () {
  'use strict';

  // Helper function to calculate original total from items
  function calculateOriginalTotal(items) {
    if (!items || !Array.isArray(items)) return 0;

    return items.reduce((total, item) => {
      const price = item.original_price || item.price || 0;
      return total + (price * item.quantity);
    }, 0);
  }

  // Currency formatting helper
  function formatMoney(amount) {
    // Use CurrencyFormatter if available, otherwise fallback
    if (window.CurrencyFormatter) {
      return window.CurrencyFormatter.format(amount);
    }

    const currency = window.Shopify?.currency?.active || cartState.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency
      }).format(Number(amount) / 100);
    } catch (error) {
      return `${currency} ${(Number(amount) / 100).toFixed(2)}`;
    }
  }

  // Shopify exposes the locale-aware storefront root (for example, `/en/`).
  function getStorefrontRoute(path = '') {
    const configuredRoot = window.Shopify?.routes?.root || '/';
    const root = configuredRoot.endsWith('/') ? configuredRoot : `${configuredRoot}/`;
    return `${root}${String(path).replace(/^\/+/, '')}`;
  }

  async function createResponseError(response, fallbackMessage) {
    let message = '';

    try {
      const payload = await response.clone().json();
      if (typeof payload?.description === 'string') {
        message = payload.description;
      } else if (typeof payload?.message === 'string') {
        message = payload.message;
      } else if (typeof payload?.errors === 'string') {
        message = payload.errors;
      } else if (payload?.errors && typeof payload.errors === 'object') {
        message = Object.values(payload.errors).flat().join(' ');
      }
    } catch (error) {
      // Some Shopify errors are plain text rather than JSON.
      try {
        const responseText = (await response.text()).trim();
        if (responseText && !responseText.startsWith('<')) {
          message = responseText;
        }
      } catch (textError) {
        // Fall back to the operation-specific message below.
      }
    }

    const cartError = new Error(message || fallbackMessage);
    cartError.status = response.status;
    return cartError;
  }

  // Cart state
  let cartState = {
    items: [],
    item_count: 0,
    total_price: 0,
    currency: 'USD' // Will be updated from Shopify cart data
  };

  // Cart drawer state
  let cartDrawerOpen = false;
  let cartDrawerTrigger = null;

  // All cart reads and writes share a queue so stale responses cannot overwrite
  // newer state. Mutations are counted as soon as they are scheduled, allowing
  // checkout and quantity controls to be guarded for the whole queue duration.
  let operationQueue = Promise.resolve();
  let pendingMutations = 0;
  let fetchDebounceTimer = null;
  let fetchDebounceWaiters = [];
  let initPromise = null;
  let listenersInitialized = false;

  // Event handlers registry
  const eventHandlers = {
    'cart:updated': [],
    'cart:item-added': [],
    'cart:item-removed': [],
    'cart:error': []
  };

  // Initialize cart
  function init() {
    if (initPromise) return initPromise;

    // The UI must exist before the first fetch publishes cart state.
    createCartDrawer();
    initializeEventListeners();
    updateCartUI();

    initPromise = fetchCart(true).catch((error) => {
      console.error('Failed to initialize cart:', error);
      return cartState;
    });

    return initPromise;
  }

  function getLineSignature(item) {
    if (!item) return '';
    const properties = Object.entries(item.properties || {}).sort(([first], [second]) => first.localeCompare(second));
    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id || '';
    return JSON.stringify([item.variant_id || item.id, properties, sellingPlanId]);
  }

  function captureDrawerFocus() {
    if (!cartDrawerOpen) return null;
    const drawer = document.getElementById('anime-cart-drawer');
    const activeElement = document.activeElement;
    if (!drawer?.contains(activeElement)) return null;

    const lineElement = activeElement.closest('.cart-drawer-item');
    if (!lineElement) return { control: 'close' };

    const currentItem = cartState.items.find((item) => item.key === lineElement.dataset.key);
    return {
      signature: getLineSignature(currentItem),
      control: activeElement.dataset.cartControl || 'quantity'
    };
  }

  function restoreDrawerFocus(focusContext) {
    if (!focusContext) return;

    window.requestAnimationFrame(() => {
      if (!cartDrawerOpen) return;
      const drawer = document.getElementById('anime-cart-drawer');
      let target = null;

      if (focusContext.signature) {
        const matchingItem = cartState.items.find((item) => getLineSignature(item) === focusContext.signature);
        const matchingLine = matchingItem
          ? [...drawer.querySelectorAll('.cart-drawer-item')].find((line) => line.dataset.key === matchingItem.key)
          : null;
        target = matchingLine?.querySelector(`[data-cart-control="${focusContext.control}"]`);
      }

      (target || drawer.querySelector('.cart-drawer-close'))?.focus();
    });
  }

  function setCartState(newCartState, focusContext = captureDrawerFocus()) {
    cartState = {
      ...newCartState,
      original_total_price: newCartState.original_total_price || calculateOriginalTotal(newCartState.items)
    };

    updateCartUI();
    if (pendingMutations > 0) {
      // updateCartUI can replace controls while a request is still running.
      updatePendingState();
    }
    triggerEvent('cart:updated', cartState);
    restoreDrawerFocus(focusContext);
    return cartState;
  }

  function updatePendingState() {
    const pending = pendingMutations > 0;
    document.documentElement.classList.toggle('cart-is-updating', pending);

    document.querySelectorAll(
      '.checkout-button, .checkout-btn, .update-cart-btn, [data-quick-add], ' +
      '.cart-drawer-item .quantity-btn, ' +
      '.cart-drawer-item .quantity-input, .cart-page .quantity-btn, ' +
      '.cart-page .quantity-input, .cart-page .remove-btn, .cart-item-remove'
    ).forEach((control) => {
      control.disabled = pending;
      control.setAttribute('aria-busy', pending ? 'true' : 'false');
      control.setAttribute('aria-disabled', pending ? 'true' : 'false');
      control.style.pointerEvents = pending ? 'none' : '';
    });

    document.querySelectorAll('.cart-page, .cart-drawer-content').forEach((region) => {
      region.setAttribute('aria-busy', pending ? 'true' : 'false');
    });

    document.querySelectorAll('.express-checkout, .payment-buttons').forEach((region) => {
      region.setAttribute('aria-disabled', pending ? 'true' : 'false');
      region.inert = pending;
      region.style.pointerEvents = pending ? 'none' : '';
    });

    if (pending) {
      showLoading();
    } else {
      hideLoading();
    }
  }

  function enqueueOperation(operation, { mutation = false } = {}) {
    if (mutation) {
      pendingMutations += 1;
      updatePendingState();
    }

    const operationPromise = operationQueue.then(() => operation());
    // Keep the shared queue usable even when a caller receives a rejection.
    operationQueue = operationPromise.catch(() => undefined);

    return operationPromise.finally(() => {
      if (mutation) {
        pendingMutations = Math.max(0, pendingMutations - 1);
        updatePendingState();
      }
    });
  }

  async function requestCart(focusContext) {
    const response = await fetch(getStorefrontRoute('cart.js'), {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw await createResponseError(response, 'Could not load your cart.');
    }

    return setCartState(await response.json(), focusContext);
  }

  function enqueueCartRead() {
    return enqueueOperation(requestCart).catch((error) => {
      console.error('Failed to fetch cart:', error);
      triggerEvent('cart:error', error);
      throw error;
    });
  }

  function settleDebouncedFetch(promise) {
    const waiters = fetchDebounceWaiters;
    fetchDebounceWaiters = [];

    promise.then(
      (cart) => waiters.forEach(({ resolve }) => resolve(cart)),
      (error) => waiters.forEach(({ reject }) => reject(error))
    );
  }

  // Fetch current cart state. Non-immediate calls are debounced, and every
  // caller is explicitly settled when the eventual request completes.
  function fetchCart(immediate = false) {
    if (immediate) {
      if (fetchDebounceTimer) {
        clearTimeout(fetchDebounceTimer);
        fetchDebounceTimer = null;
      }

      const requestPromise = enqueueCartRead();
      if (fetchDebounceWaiters.length > 0) {
        settleDebouncedFetch(requestPromise);
      }
      return requestPromise;
    }

    return new Promise((resolve, reject) => {
      fetchDebounceWaiters.push({ resolve, reject });

      if (fetchDebounceTimer) {
        clearTimeout(fetchDebounceTimer);
      }

      fetchDebounceTimer = setTimeout(() => {
        fetchDebounceTimer = null;
        settleDebouncedFetch(enqueueCartRead());
      }, 300);
    });
  }

  function reportCartError(error, fallbackMessage) {
    const message = error?.message || fallbackMessage;
    if (error && typeof error === 'object') {
      error.cartReported = true;
    }
    console.error(fallbackMessage, error);
    showError(message);
    triggerEvent('cart:error', error);
  }

  // Add item to cart
  function addItem(variantId, quantity = 1, properties = {}, triggerElement = document.activeElement) {
    const drawerTrigger = triggerElement instanceof HTMLElement && triggerElement !== document.body
      ? triggerElement
      : null;
    const operation = enqueueOperation(async () => {
      const formData = {
        id: variantId,
        quantity,
        properties
      };

      const response = await fetch(getStorefrontRoute('cart/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw await createResponseError(response, 'Could not add this item to your cart.');
      }

      const item = await response.json();
      let refreshFailed = false;
      try {
        await requestCart();
      } catch (refreshError) {
        // The add already succeeded. Never report it as a failed add because a
        // retry would duplicate the line; fall back to Shopify's rendered cart.
        refreshFailed = true;
        console.error('Item was added, but the cart could not be refreshed:', refreshError);
        triggerEvent('cart:error', refreshError);
      }

      // Show success animation
      showAddToCartSuccess(item);

      if (refreshFailed) {
        showToast('Item added. Opening your cart…', 'info');
        window.setTimeout(() => window.location.assign(getStorefrontRoute('cart')), 300);
        triggerEvent('cart:item-added', { item, cart: cartState, refreshFailed: true });
        return item;
      }

      // Open cart drawer
      openCartDrawer(drawerTrigger);

      triggerEvent('cart:item-added', { item, cart: cartState });
      return item;
    }, { mutation: true });

    return operation.catch((error) => {
      reportCartError(error, 'Could not add this item to your cart.');
      throw error;
    });
  }

  // Update item quantity. Requests are serialized instead of being dropped.
  function updateItem(key, quantity) {
    // Prevent negative quantities
    quantity = Math.max(0, quantity);
    // Capture before enqueueOperation disables the active drawer control.
    const focusContext = captureDrawerFocus();

    const operation = enqueueOperation(async () => {
      const response = await fetch(getStorefrontRoute('cart/change.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) {
        throw await createResponseError(response, 'Could not update that quantity.');
      }

      const updatedCart = await response.json();
      setCartState(updatedCart, focusContext);

      if (quantity === 0) {
        triggerEvent('cart:item-removed', { key });
      }

      return cartState;
    }, { mutation: true });

    return operation.catch(async (error) => {
      reportCartError(error, 'Could not update that quantity.');

      // Reconcile with Shopify after a rejected mutation, then preserve the
      // original failure for callers that need to restore optimistic UI.
      try {
        await enqueueOperation(() => requestCart(focusContext), { mutation: true });
      } catch (fetchError) {
        console.error('Failed to refresh cart after error:', fetchError);
      }

      throw error;
    });
  }

  // Remove item from cart
  async function removeItem(key) {
    return updateItem(key, 0);
  }

  // Clear entire cart
  function clearCart() {
    const operation = enqueueOperation(async () => {
      const response = await fetch(getStorefrontRoute('cart/clear.js'), {
        method: 'POST',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw await createResponseError(response, 'Could not clear your cart.');
      }

      setCartState(await response.json());
      return cartState;
    }, { mutation: true });

    return operation.catch((error) => {
      reportCartError(error, 'Could not clear your cart.');
      throw error;
    });
  }

  // Create cart drawer HTML
  function createCartDrawer() {
    if (document.getElementById('anime-cart-drawer')) return;

    const drawer = document.createElement('div');
    drawer.id = 'anime-cart-drawer';
    drawer.className = 'anime-cart-drawer';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.inert = true;
    // Create cart drawer structure safely
    const overlay = document.createElement('div');
    overlay.className = 'cart-drawer-overlay';
    overlay.onclick = () => AnimeCart.closeCartDrawer();

    const content = document.createElement('div');
    content.className = 'cart-drawer-content';
    content.setAttribute('role', 'dialog');
    content.setAttribute('aria-modal', 'true');
    content.setAttribute('aria-labelledby', 'cart-drawer-title');

    // Header
    const header = document.createElement('div');
    header.className = 'cart-drawer-header';

    const title = document.createElement('h3');
    title.className = 'cart-drawer-title';
    title.id = 'cart-drawer-title';
    title.textContent = 'Your Anime Haul ';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'cart-drawer-icon');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M7 18C5.9 18 5.01 18.9 5.01 20C5.01 21.1 5.9 22 7 22C8.1 22 9 21.1 9 20C9 18.9 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.25 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.59 17.3 11.97L20.88 5.48C20.96 5.34 21 5.17 21 5C21 4.45 20.55 4 20 4H5.21L4.27 2H1ZM17 18C15.9 18 15.01 18.9 15.01 20C15.01 21.1 15.9 22 17 22C18.1 22 19 21.1 19 20C19 18.9 18.1 18 17 18Z');
    path.setAttribute('fill', 'currentColor');
    svg.appendChild(path);
    title.appendChild(svg);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'cart-drawer-close';
    closeBtn.textContent = '✕';
    closeBtn.setAttribute('aria-label', 'Close cart');
    closeBtn.onclick = () => AnimeCart.closeCartDrawer();

    header.appendChild(title);
    header.appendChild(closeBtn);

    // Body
    const body = document.createElement('div');
    body.className = 'cart-drawer-body';

    const items = document.createElement('div');
    items.className = 'cart-drawer-items';
    items.id = 'cart-drawer-items';

    const empty = document.createElement('div');
    empty.className = 'cart-drawer-empty';
    empty.id = 'cart-drawer-empty';
    empty.style.display = 'none';

    const emptyMsg = document.createElement('p');
    emptyMsg.className = 'empty-message';
    emptyMsg.textContent = 'Your cart is as empty as my soul 💔';

    const emptyBtn = document.createElement('button');
    emptyBtn.type = 'button';
    emptyBtn.className = 'anime-button';
    emptyBtn.textContent = 'Continue Shopping';
    emptyBtn.onclick = () => AnimeCart.closeCartDrawer();

    empty.appendChild(emptyMsg);
    empty.appendChild(emptyBtn);

    body.appendChild(items);
    body.appendChild(empty);

    // Footer
    const footer = document.createElement('div');
    footer.className = 'cart-drawer-footer';
    footer.id = 'cart-drawer-footer';

    // Build footer content safely
    footer.innerHTML = `
      <div class="cart-summary-lines">
        <div class="cart-line cart-subtotal">
          <span class="cart-label">Subtotal</span>
          <span class="cart-value" id="cart-subtotal-price">$0.00</span>
        </div>
        <div class="cart-line cart-savings-line" id="cart-savings" style="display: none;">
          <span class="cart-label">Savings</span>
          <span class="cart-value cart-savings-amount" id="cart-savings-amount">-$0.00</span>
        </div>
        <div class="cart-line cart-total-line" id="cart-total-line" style="display: none;">
          <span class="cart-label">Total</span>
          <span class="cart-value cart-total-price" id="cart-total-price">$0.00</span>
        </div>
      </div>
      <div class="cart-shipping-bar" id="cart-shipping-bar"></div>
      <div class="cart-actions">
        <button type="button" class="anime-button checkout-button">
          CHECKOUT NOW
        </button>
      </div>
    `;

    const checkoutButton = footer.querySelector('.checkout-button');
    checkoutButton.addEventListener('click', () => {
      if (pendingMutations > 0) return;
      window.location.assign(getStorefrontRoute('checkout'));
    });

    // Assemble content
    content.appendChild(header);
    content.appendChild(body);
    content.appendChild(footer);

    drawer.appendChild(overlay);
    drawer.appendChild(content);

    document.body.appendChild(drawer);

    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeCartDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll('button:not([disabled]), a[href], input:not([disabled])')];
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Add styles
    addCartDrawerStyles();
    updateCartDrawer();
  }

  // Add cart drawer styles
  function addCartDrawerStyles() {
    if (document.getElementById('anime-cart-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'anime-cart-styles';
    styles.textContent = `
      .anime-cart-drawer {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 9999;
        pointer-events: none;
      }
      
      .anime-cart-drawer.active {
        pointer-events: all;
      }
      
      .cart-drawer-overlay {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: rgba(0, 0, 0, 0.7);
        opacity: 0;
        transition: opacity 0.3s ease;
      }
      
      .anime-cart-drawer.active .cart-drawer-overlay {
        opacity: 1;
      }
      
      .cart-drawer-content {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 400px;
        max-width: 85vw;
        background: var(--color-background);
        transform: translateX(100%);
        transition: transform 0.3s ease;
        display: flex;
        flex-direction: column;
        box-shadow: -5px 0 30px rgba(0, 0, 0, 0.3);
        overflow: hidden;
      }
      
      .anime-cart-drawer.active .cart-drawer-content {
        transform: translateX(0);
      }
      
      /* Desktop-only active state */
      @media (min-width: 1025px) {
        .anime-cart-drawer.active .cart-drawer-content {
          transform: translateX(0) !important;
        }
      }
      
      .cart-drawer-header {
        padding: clamp(1rem, 3vw, 1.5rem);
        border-bottom: 2px solid var(--color-primary);
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-sizing: border-box;
      }
      
      .cart-drawer-title {
        font-size: clamp(1.25rem, 4vw, 1.5rem);
        color: var(--color-primary);
        margin: 0;
        word-break: break-word;
      }
      
      .cart-drawer-icon {
        display: inline-block;
        width: 1.5rem;
        height: 1.5rem;
        vertical-align: middle;
        margin-left: 0.5rem;
        color: var(--color-primary);
      }
      
      .cart-drawer-close {
        background: none;
        border: none;
        font-size: 1.5rem;
        color: var(--color-foreground);
        cursor: pointer;
        padding: 0.5rem;
        transition: transform 0.2s;
      }
      
      .cart-drawer-close:hover {
        transform: scale(1.1);
      }
      
      .cart-drawer-body {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: clamp(1rem, 3vw, 1.5rem);
        box-sizing: border-box;
      }
      
      .cart-drawer-empty {
        text-align: center;
        padding: 3rem 1rem;
      }
      
      .empty-message {
        font-size: 1.25rem;
        color: var(--color-accent);
        margin-bottom: 2rem;
      }
      
      .cart-drawer-item {
        display: flex;
        gap: clamp(0.5rem, 2vw, 1rem);
        padding: 1rem 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        box-sizing: border-box;
      }
      
      .cart-item-image {
        width: clamp(60px, 15vw, 80px);
        height: clamp(60px, 15vw, 80px);
        object-fit: cover;
        border-radius: 10px;
        flex-shrink: 0;
      }
      
      .cart-item-details {
        flex: 1;
        min-width: 0;
        overflow: hidden;
      }
      
      .cart-item-title {
        font-weight: 600;
        margin-bottom: 0.5rem;
        color: var(--color-primary);
        font-size: clamp(0.875rem, 3vw, 1rem);
        word-break: break-word;
        overflow-wrap: break-word;
      }
      
      .cart-item-variant {
        font-size: clamp(0.75rem, 2.5vw, 0.875rem);
        color: var(--color-accent);
        margin-bottom: 0.5rem;
        word-break: break-word;
      }

      .cart-item-properties {
        margin: 0 0 0.5rem;
        font-size: clamp(0.6875rem, 2.25vw, 0.8125rem);
        color: var(--color-foreground);
        opacity: 0.78;
      }

      .cart-item-property {
        display: block;
        overflow-wrap: anywhere;
      }

      .cart-item-property dt,
      .cart-item-property dd {
        display: inline;
      }

      .cart-item-property dt {
        font-weight: 600;
      }

      .cart-item-property dd {
        margin: 0;
      }
      
      .cart-item-price {
        font-weight: 600;
        font-size: clamp(0.875rem, 3vw, 1rem);
      }
      
      .cart-item-quantity {
        display: flex;
        align-items: center;
        gap: clamp(0.25rem, 2vw, 0.5rem);
        margin-top: 0.5rem;
        flex-wrap: wrap;
      }
      
      .quantity-btn {
        width: clamp(28px, 6vw, 30px);
        height: clamp(28px, 6vw, 30px);
        border: 1px solid var(--color-primary);
        background: transparent;
        color: var(--color-primary);
        border-radius: 50%;
        cursor: pointer;
        transition: all 0.2s;
        font-size: clamp(0.75rem, 3vw, 1rem);
      }
      
      .quantity-btn:hover {
        background: var(--color-primary);
        color: white;
      }
      
      .quantity-input {
        width: clamp(40px, 10vw, 50px);
        text-align: center;
        border: 1px solid rgba(255, 255, 255, 0.2);
        background: rgba(255, 255, 255, 0.1);
        color: var(--color-foreground);
        padding: 0.25rem;
        border-radius: 5px;
        font-size: clamp(0.75rem, 2.5vw, 0.875rem);
        box-sizing: border-box;
      }
      
      .cart-item-remove {
        align-self: flex-start;
        padding: 0;
        border: 0;
        background: transparent;
        color: #b42318;
        font: inherit;
        font-size: 0.875rem;
        cursor: pointer;
        text-decoration: underline;
      }
      
      .cart-drawer-footer {
        padding: clamp(1rem, 3vw, 1.5rem);
        border-top: 2px solid var(--color-primary);
        background: rgba(255, 255, 255, 0.05);
        box-sizing: border-box;
        overflow: hidden;
      }
      
      .cart-summary-lines {
        margin-bottom: 1rem;
        padding: 0.25rem 0;
        font-family: monospace;
        font-size: 0.9375rem;
      }
      
      .cart-line {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.375rem;
        line-height: 1.3;
      }
      
      .cart-label {
        font-weight: 500;
        text-transform: capitalize;
        font-size: 0.875rem;
      }
      
      .cart-label::after {
        content: ':';
        margin-right: 0.5rem;
      }
      
      .cart-value {
        text-align: right;
        font-weight: 600;
        font-size: 0.875rem;
      }
      
      .cart-savings-amount {
        color: #10B981;
      }
      
      /* Make total line bigger and bolder */
      .cart-total-line {
        margin-top: 0.5rem;
        padding-top: 0.5rem;
        border-top: 2px solid rgba(255, 255, 255, 0.1);
      }
      
      .cart-total-line .cart-label {
        font-weight: 700;
        font-size: 1.125rem;
      }
      
      .cart-total-price {
        font-weight: 700;
        font-size: 1.25rem;
        color: var(--color-primary);
      }
      
      .cart-shipping-bar {
        margin-bottom: 1rem;
      }
      
      .shipping-progress {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 5px;
        overflow: hidden;
        margin-bottom: 0.5rem;
      }
      
      .shipping-progress-fill {
        background: var(--color-primary);
        height: 100%;
        transition: width 0.3s ease;
      }
      
      .shipping-message {
        text-align: center;
        font-size: 0.875rem;
        color: var(--color-accent);
      }
      
      .cart-actions {
        margin-bottom: 1rem;
      }
      
      .checkout-button {
        width: 100%;
        padding: 0.75rem 1rem;
        font-size: 1rem;
        box-sizing: border-box;
        word-break: break-word;
      }
      
      .cart-payment-methods {
        text-align: center;
        opacity: 0.7;
      }
      
      /* Loading overlay */
      .cart-loading {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
      }
      
      .loading-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(255, 255, 255, 0.3);
        border-top-color: var(--color-primary);
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      /* Success animation */
      .add-success-popup {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0);
        background: var(--color-success);
        color: white;
        padding: 2rem;
        border-radius: 20px;
        text-align: center;
        z-index: 10000;
        animation: successPop 0.5s ease forwards;
      }
      
      @keyframes successPop {
        to {
          transform: translate(-50%, -50%) scale(1);
        }
      }
      
      .success-icon {
        font-size: 3rem;
        margin-bottom: 1rem;
        display: block;
      }
      
      .success-message {
        font-size: 1.25rem;
        font-weight: 600;
      }
      
      /* Large tablet/small desktop optimization */
      @media (min-width: 1025px) and (max-width: 1200px) {
        .cart-drawer-content {
          width: 380px;
          max-width: 85vw;
        }
      }
      
      /* Mobile breakpoints - Full screen overlay */
      @media (max-width: 1024px) {
        .cart-drawer-content {
          position: fixed;
          top: auto;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100vw;
          height: 100vh;
          max-width: none;
          transform: translateY(100%);
          border-radius: 0;
          transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .anime-cart-drawer.active .cart-drawer-content {
          transform: translateY(0) !important;
        }
        
        .cart-drawer-header {
          padding: 1.5rem 1rem;
          border-bottom: 2px solid var(--color-primary);
        }
        
        .cart-drawer-body {
          padding: 1rem;
          flex: 1;
        }
        
        .cart-drawer-footer {
          padding: 1rem;
          border-top: 2px solid var(--color-primary);
        }
        
        .cart-drawer-close {
          font-size: 2rem;
          padding: 1rem;
        }
        
        .cart-drawer-title {
          font-size: 1.5rem;
        }
        
        .cart-drawer-item {
          gap: 1rem;
          padding: 1.5rem 0;
        }
        
        .cart-item-image {
          width: 100px;
          height: 100px;
        }
        
        .cart-item-title {
          font-size: 1.125rem;
        }
        
        .cart-item-price {
          font-size: 1.125rem;
        }
        
        .cart-subtotal {
          margin-bottom: 1.5rem;
        }
        
        .checkout-button {
          padding: 0.875rem 1rem;
          font-size: 1.125rem;
        }
      }
    `;

    document.head.appendChild(styles);
  }

  // Open cart drawer
  function openCartDrawer(triggerElement) {
    const drawer = document.getElementById('anime-cart-drawer');
    if (drawer) {
      if (!cartDrawerOpen) {
        const activeElement = document.activeElement;
        const focusCandidate = triggerElement instanceof HTMLElement ? triggerElement : activeElement;
        cartDrawerTrigger = focusCandidate instanceof HTMLElement && focusCandidate !== document.body
          ? focusCandidate
          : null;
      }
      drawer.inert = false;
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('active');
      cartDrawerOpen = true;
      document.body.style.overflow = 'hidden';
      window.requestAnimationFrame(() => drawer.querySelector('.cart-drawer-close')?.focus());
    }
  }

  // Close cart drawer
  function closeCartDrawer() {
    const drawer = document.getElementById('anime-cart-drawer');
    if (drawer) {
      const focusTarget = cartDrawerTrigger;
      if (focusTarget?.isConnected) {
        focusTarget.focus({ preventScroll: true });
      } else {
        document.getElementById('MainContent')?.focus({ preventScroll: true });
      }
      drawer.classList.remove('active');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.inert = true;
      cartDrawerOpen = false;
      document.body.style.overflow = '';
      cartDrawerTrigger = null;
    }
  }

  // Update cart UI
  function updateCartUI() {
    updateCartCount();
    updateCartDrawer();
    updateCartPage();
    updateHeaderCartPreview();
  }

  // Update cart count in header
  function updateCartCount() {
    const cartLinks = document.querySelectorAll('.cart-link');
    cartLinks.forEach(link => {
      link.setAttribute('aria-label', `Cart, ${cartState.item_count} ${cartState.item_count === 1 ? 'item' : 'items'}`);
      // Look for existing cart count element
      let countEl = link.querySelector('.cart-count');

      if (cartState.item_count > 0) {
        // Only create element if it doesn't exist
        if (!countEl) {
          countEl = document.createElement('span');
          countEl.className = 'cart-count';
          // Append to cart link to avoid positioning issues
          link.appendChild(countEl);
        }

        // Update the count
        countEl.textContent = cartState.item_count;

        // Animate count change
        countEl.style.transform = 'scale(1.3)';
        setTimeout(() => {
          if (countEl) {
            countEl.style.transform = 'scale(1)';
          }
        }, 300);
      } else if (countEl) {
        // Remove count element if cart is empty
        countEl.remove();
      }
    });
  }

  // Update cart drawer content
  function updateCartDrawer() {
    const itemsContainer = document.getElementById('cart-drawer-items');
    const emptyContainer = document.getElementById('cart-drawer-empty');
    const footerContainer = document.getElementById('cart-drawer-footer');
    const subtotalEl = document.getElementById('cart-subtotal-price');

    if (!itemsContainer) return;

    if (cartState.items.length === 0) {
      itemsContainer.style.display = 'none';
      emptyContainer.style.display = 'block';
      footerContainer.style.display = 'none';
    } else {
      itemsContainer.style.display = 'block';
      emptyContainer.style.display = 'none';
      footerContainer.style.display = 'block';

      // Render cart items safely
      itemsContainer.innerHTML = ''; // Clear existing items

      cartState.items.forEach(item => {
        const itemEl = document.createElement('div');
        itemEl.className = 'cart-drawer-item';
        itemEl.setAttribute('data-key', item.key);
        const visibleProperties = Object.entries(item.properties || {})
          .filter(([name, value]) => value && !name.startsWith('_'));
        const propertyLabel = visibleProperties
          .map(([name, value]) => `${name}: ${value}`)
          .join(', ');
        const itemLabel = [item.product_title, item.variant_title, propertyLabel]
          .filter(Boolean)
          .join(' – ') || 'item';

        // Image
        const img = document.createElement('img');
        img.src = item.image || '';
        img.alt = item.title || '';
        img.className = 'cart-item-image';
        img.width = 80;
        img.height = 80;

        // Details container
        const details = document.createElement('div');
        details.className = 'cart-item-details';

        // Title
        const titleEl = document.createElement('div');
        titleEl.className = 'cart-item-title';
        titleEl.textContent = item.product_title || '';
        details.appendChild(titleEl);

        // Variant
        if (item.variant_title) {
          const variantEl = document.createElement('div');
          variantEl.className = 'cart-item-variant';
          variantEl.textContent = item.variant_title;
          details.appendChild(variantEl);
        }

        if (visibleProperties.length > 0) {
          const propertiesEl = document.createElement('dl');
          propertiesEl.className = 'cart-item-properties';
          visibleProperties.forEach(([name, value]) => {
            const propertyEl = document.createElement('div');
            propertyEl.className = 'cart-item-property';
            const propertyNameEl = document.createElement('dt');
            propertyNameEl.textContent = `${name}:`;
            const propertyValueEl = document.createElement('dd');
            propertyValueEl.textContent = value;
            propertyEl.append(propertyNameEl, propertyValueEl);
            propertiesEl.appendChild(propertyEl);
          });
          details.appendChild(propertiesEl);
        }

        // Price (show original price)
        const priceEl = document.createElement('div');
        priceEl.className = 'cart-item-price';
        priceEl.textContent = formatMoney(item.original_price || item.price);
        details.appendChild(priceEl);

        // Quantity controls
        const qtyContainer = document.createElement('div');
        qtyContainer.className = 'cart-item-quantity';

        const minusBtn = document.createElement('button');
        minusBtn.type = 'button';
        minusBtn.className = 'quantity-btn';
        minusBtn.dataset.cartControl = 'decrease';
        minusBtn.setAttribute('aria-label', `Decrease quantity for ${itemLabel}`);
        minusBtn.textContent = '-';
        minusBtn.onclick = (e) => {
          e.preventDefault();
          if (pendingMutations > 0) return;
          const currentQty = parseInt(qtyInput.value) || 0;
          AnimeCart.updateItem(item.key, currentQty - 1).catch(() => undefined);
        };

        const qtyInput = document.createElement('input');
        qtyInput.type = 'number';
        qtyInput.className = 'quantity-input';
        qtyInput.dataset.cartControl = 'quantity';
        qtyInput.value = item.quantity;
        qtyInput.min = '0';
        qtyInput.setAttribute('aria-label', `Quantity for ${itemLabel}`);
        qtyInput.onchange = async (e) => {
          if (pendingMutations > 0) return;
          const newValue = parseInt(e.target.value) || 0;
          if (newValue !== item.quantity) {
            try {
              await AnimeCart.updateItem(item.key, newValue);
            } catch (error) {
              e.target.value = item.quantity;
            }
          }
        };

        const plusBtn = document.createElement('button');
        plusBtn.type = 'button';
        plusBtn.className = 'quantity-btn';
        plusBtn.dataset.cartControl = 'increase';
        plusBtn.setAttribute('aria-label', `Increase quantity for ${itemLabel}`);
        plusBtn.textContent = '+';
        plusBtn.onclick = (e) => {
          e.preventDefault();
          if (pendingMutations > 0) return;
          const currentQty = parseInt(qtyInput.value) || 0;
          AnimeCart.updateItem(item.key, currentQty + 1).catch(() => undefined);
        };

        qtyContainer.appendChild(minusBtn);
        qtyContainer.appendChild(qtyInput);
        qtyContainer.appendChild(plusBtn);
        details.appendChild(qtyContainer);

        // Remove button
        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'cart-item-remove';
        removeButton.dataset.cartControl = 'remove';
        removeButton.textContent = 'Remove';
        removeButton.setAttribute('aria-label', `Remove ${itemLabel} from cart`);
        removeButton.onclick = (e) => {
          e.preventDefault();
          if (pendingMutations > 0) return;
          AnimeCart.removeItem(item.key).catch(() => undefined);
        };
        details.appendChild(removeButton);

        // Assemble item
        itemEl.appendChild(img);
        itemEl.appendChild(details);
        itemsContainer.appendChild(itemEl);
      });

      // Calculate original total price
      let originalTotal = 0;
      cartState.items.forEach(item => {
        const itemPrice = item.original_price || item.price;
        originalTotal += itemPrice * item.quantity;
      });

      // Update subtotal to show original price
      if (subtotalEl) {
        subtotalEl.textContent = formatMoney(originalTotal);
      }

      // Show discount if there is one
      const discountAmount = originalTotal - cartState.total_price;
      const savingsContainer = document.getElementById('cart-savings');
      const savingsAmountEl = document.getElementById('cart-savings-amount');

      if (discountAmount > 0 && savingsContainer && savingsAmountEl) {
        savingsContainer.style.display = '';
        savingsAmountEl.textContent = formatMoney(-discountAmount);

        // Also show total line
        const totalLine = document.getElementById('cart-total-line');
        const totalPriceEl = document.getElementById('cart-total-price');
        if (totalLine && totalPriceEl) {
          totalLine.style.display = '';
          totalPriceEl.textContent = formatMoney(cartState.total_price);
        }
      } else if (savingsContainer) {
        savingsContainer.style.display = 'none';
        const totalLine = document.getElementById('cart-total-line');
        if (totalLine) {
          totalLine.style.display = 'none';
        }
      }

      // Update shipping bar
      updateShippingBar();
    }
  }

  // Update cart page if we're on it
  function updateCartPage() {
    const cartPath = getStorefrontRoute('cart').replace(/\/$/, '');
    if (window.location.pathname.replace(/\/$/, '') === cartPath) {
      // Reload cart page content via AJAX if needed
      // For now, we'll let the enhanced cart page handle its own updates
    }
  }

  // Update header cart preview
  function updateHeaderCartPreview() {
    const previewContent = document.querySelector('.cart-preview-content');
    if (!previewContent) return;

    const previewItems = previewContent.querySelector('.cart-preview-items');
    const previewEmpty = previewContent.querySelector('.cart-preview-empty');
    const previewFooter = previewContent.querySelector('.cart-preview-footer');
    const previewTotal = previewContent.querySelector('.cart-preview-total-price');
    const previewMore = previewContent.querySelector('.cart-preview-more');

    if (cartState.items.length === 0) {
      // Show empty state
      if (previewItems) previewItems.style.display = 'none';
      if (previewFooter) previewFooter.style.display = 'none';
      if (previewEmpty) {
        previewEmpty.style.display = 'block';
        previewEmpty.textContent = 'Your cart is empty';
      }
      
      // Show shop button
      const shopBtn = previewContent.querySelector('.cart-preview-shop');
      if (shopBtn) shopBtn.style.display = 'block';
    } else {
      // Show cart items
      if (previewEmpty) previewEmpty.style.display = 'none';
      if (previewItems) previewItems.style.display = 'block';
      if (previewFooter) previewFooter.style.display = 'block';
      
      // Hide shop button
      const shopBtn = previewContent.querySelector('.cart-preview-shop');
      if (shopBtn) shopBtn.style.display = 'none';

      // Update items (show first 3)
      if (previewItems) {
        previewItems.innerHTML = '';
        cartState.items.slice(0, 3).forEach(item => {
          const itemEl = document.createElement('div');
          itemEl.className = 'cart-preview-item';
          
          const img = document.createElement('img');
          img.src = item.image || '';
          img.alt = item.title || '';
          img.width = 60;
          img.height = 60;
          
          const details = document.createElement('div');
          details.className = 'cart-preview-item-details';
          
          const title = document.createElement('a');
          title.href = item.url || '#';
          title.className = 'cart-preview-item-title';
          title.textContent = (item.product_title || '').substring(0, 30) + (item.product_title && item.product_title.length > 30 ? '...' : '');
          
          const price = document.createElement('p');
          price.className = 'cart-preview-item-price';
          const lineTotal = item.line_price ?? ((item.price ?? 0) * item.quantity);
          price.textContent = `${item.quantity} ${item.quantity === 1 ? 'item' : 'items'} · ${formatMoney(lineTotal)}`;
          
          details.appendChild(title);
          details.appendChild(price);
          itemEl.appendChild(img);
          itemEl.appendChild(details);
          previewItems.appendChild(itemEl);
        });
      }

      // Update "more items" text
      if (previewMore) {
        if (cartState.items.length > 3) {
          previewMore.style.display = 'block';
          previewMore.textContent = `+ ${cartState.items.length - 3} more items`;
        } else {
          previewMore.style.display = 'none';
        }
      }

      // Update total
      if (previewTotal) {
        previewTotal.textContent = formatMoney(cartState.total_price);
      }
    }
  }

  // Update shipping progress bar
  function updateShippingBar() {
    const shippingBar = document.getElementById('cart-shipping-bar');
    if (!shippingBar) return;

    const freeShippingThreshold = Number(window.themeSettings?.freeShippingItemThreshold) || 1;
    const totalTowels = cartState.item_count;
    const towelsNeeded = Math.max(0, freeShippingThreshold - totalTowels);

    // Calculate progress
    const progress = Math.min(100, (totalTowels / freeShippingThreshold) * 100);

    let message;
    if (towelsNeeded > 0) {
      const towelText = towelsNeeded === 1 ? 'towel' : 'towels';
      message = `Add ${towelsNeeded} more ${towelText} for FREE shipping! 🚚`;
    } else {
      message = `✨ You've unlocked FREE shipping! 🎉`;
    }

    // Clear and rebuild shipping bar safely
    shippingBar.innerHTML = '';

    const progressContainer = document.createElement('div');
    progressContainer.className = 'shipping-progress';

    const progressFill = document.createElement('div');
    progressFill.className = 'shipping-progress-fill';
    progressFill.style.width = `${progress}%`;
    progressContainer.appendChild(progressFill);

    const messageEl = document.createElement('div');
    messageEl.className = 'shipping-message';
    messageEl.textContent = message;

    shippingBar.appendChild(progressContainer);
    shippingBar.appendChild(messageEl);
  }


  // Show loading state
  function showLoading() {
    const drawer = document.querySelector('.cart-drawer-content');
    if (!drawer || drawer.querySelector('.cart-loading')) return;

    const loading = document.createElement('div');
    loading.className = 'cart-loading';
    loading.innerHTML = '<div class="loading-spinner"></div>';
    drawer.appendChild(loading);
  }

  // Hide loading state
  function hideLoading() {
    document.querySelectorAll('.cart-loading').forEach((loading) => loading.remove());
  }

  // Show add to cart success
  function showAddToCartSuccess(item) {
    const popup = document.createElement('div');
    popup.className = 'add-success-popup';
    popup.innerHTML = `
      <span class="success-icon">✨</span>
      <div class="success-message">Added to your haul!</div>
    `;

    document.body.appendChild(popup);

    setTimeout(() => {
      popup.remove();
    }, 2000);
  }

  // Show error message
  function showError(message) {
    showToast(message, 'error');
  }

  // Show toast notification
  function showToast(message, type = 'info') {
    const toast = document.createElement('div');
    toast.className = `anime-toast ${type}`;
    toast.textContent = message;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toast.style.cssText = `
      position: fixed;
      bottom: 2rem;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === 'error' ? 'var(--color-warning)' : 'var(--color-success)'};
      color: var(--color-text-dark, #1a1a1a);
      padding: 1rem 2rem;
      border-radius: 25px;
      font-weight: 600;
      z-index: 10000;
      animation: slideUp 0.3s ease;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  }

  // Store event listeners for cleanup
  let eventListeners = [];

  // Initialize event listeners
  function initializeEventListeners() {
    if (listenersInitialized) return;
    listenersInitialized = true;

    // Add to cart form submissions
    const formSubmitHandler = function (e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      // The product page owns its multi-item bundle submission flow.
      if (form.matches('.product-form-anime')) return;

      if (pendingMutations > 0 && form.querySelector('[name="checkout"]')) {
        e.preventDefault();
        return;
      }

      if (form.action && form.action.includes('/cart/add')) {
        e.preventDefault();
        handleAddToCart(form, e.submitter);
      }
    };
    document.addEventListener('submit', formSubmitHandler);
    eventListeners.push({ type: 'submit', handler: formSubmitHandler });

    // Quick add buttons
    const clickHandler = function (e) {
      const quickAddButton = e.target.closest('[data-quick-add]');
      if (quickAddButton) {
        e.preventDefault();
        if (pendingMutations > 0) return;

        const variantId = quickAddButton.dataset.variantId;
        if (variantId) {
          addItem(variantId, 1, {}, quickAddButton).catch(() => undefined);
        }
      }
    };
    document.addEventListener('click', clickHandler);
    eventListeners.push({ type: 'click', handler: clickHandler });
  }

  // Cleanup event listeners
  function cleanup() {
    eventListeners.forEach(({ type, handler }) => {
      document.removeEventListener(type, handler);
    });
    eventListeners = [];
    listenersInitialized = false;

    if (fetchDebounceTimer) {
      clearTimeout(fetchDebounceTimer);
      fetchDebounceTimer = null;
    }

    // Never leave callers waiting if cleanup happens during the debounce.
    if (fetchDebounceWaiters.length > 0) {
      fetchDebounceWaiters.forEach(({ resolve }) => resolve(cartState));
      fetchDebounceWaiters = [];
    }

    initPromise = null;
  }

  // Handle add to cart form submission
  async function handleAddToCart(form, triggerElement) {
    try {
      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || 1);

      // Validate inputs
      if (!variantId) {
        throw new Error('No variant ID provided');
      }

      if (isNaN(quantity) || quantity < 1) {
        throw new Error('Invalid quantity');
      }

      // Get any custom properties
      const properties = {};
      for (let [key, value] of formData.entries()) {
        if (key.startsWith('properties[') && key.endsWith(']')) {
          const propName = key.slice(11, -1);
          properties[propName] = value;
        }
      }

      await addItem(variantId, quantity, properties, triggerElement);
    } catch (error) {
      console.error('Error handling add to cart form:', error);
      // Network/API failures are already surfaced by addItem. Validation
      // errors originate here and still need a customer-facing message.
      if (!error?.cartReported) {
        showError(error?.message || 'Could not add this item to your cart.');
      }
    }
  }

  // Event management
  function on(event, handler) {
    if (eventHandlers[event] && typeof handler === 'function') {
      eventHandlers[event].push(handler);
    }
  }

  function off(event, handler) {
    if (eventHandlers[event]) {
      if (typeof handler !== 'function') {
        eventHandlers[event] = [];
        return;
      }

      const index = eventHandlers[event].indexOf(handler);
      if (index > -1) {
        eventHandlers[event].splice(index, 1);
      }
    }
  }

  function triggerEvent(event, data) {
    if (eventHandlers[event]) {
      eventHandlers[event].slice().forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          // A storefront extension must not turn a successful cart request
          // into a rejected operation for every other subscriber.
          console.error(`AnimeCart subscriber failed for ${event}:`, error);
        }
      });
    }
  }

  // Public API
  return {
    init,
    fetchCart,
    addItem,
    updateItem,
    removeItem,
    clearCart,
    openCartDrawer,
    closeCartDrawer,
    showToast,
    on,
    off,
    getCart: () => cartState,
    isUpdating: () => pendingMutations > 0,
    isDrawerOpen: () => cartDrawerOpen,
    cleanup
  };
})();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', AnimeCart.init);
} else {
  AnimeCart.init();
}

// Back/forward cache can revive a page with an old badge or drawer state.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) AnimeCart.fetchCart(true).catch(() => undefined);
});
